import { createSelector } from 'reselect';
import pick from 'lodash/pick';

/**
 * Get the preference settings given a key or array of keys
 * @param {Object} state - current application state/store
 * @param {(String|String[])} settingsKey - a single string setting key or array of string setting keys
 * @param {String[]} [fields=null] - (optional) array of fields / key to be returned with the object
 * @param {String} [locationId=null] - (optional)
 * @param {String} [subsidiaryId=null] - (optional)
 *
 * @returns {Object}
 **/
export const getParticularPreferenceSettings = createSelector(
  [
    (state, settingsKey, fields) => state.preference.preferences,
    (state, settingsKey, fields) => settingsKey,
    (state, settingsKey, fields) => fields,
    (state, settingsKey, fields, locationId, subsidiaryId) => locationId,
    (state, settingsKey, fields, locationId, subsidiaryId) => subsidiaryId,
  ],
  (preferences = [], settingsKey = [], fields = null, locationId, subsidiaryId) => {
    // Find settings in Location > Subsidiary > Account > Device

    if (!settingsKey) return undefined;

    // If only one setting is given
    if (!Array.isArray(settingsKey)) {

      const pLocationLvl = locationId ? (preferences['location'][locationId] || {})[settingsKey] : undefined;
      const pSubsidiaryLvl = subsidiaryId ? (preferences['subsidiary'][subsidiaryId] || {})[settingsKey] : undefined;
      const pAccountLvl = preferences['account'][settingsKey];
      const setting = pLocationLvl || pSubsidiaryLvl || pAccountLvl;

      return !setting || !fields || !Array.isArray(fields) ? setting : pick(setting, ...fields);
    }

    // If array of settings is given
    return settingsKey.reduce((res, key) => {
      const pLocationLvl = locationId ? (preferences['location'][locationId] || {})[key] : undefined;
      const pSubsidiaryLvl = subsidiaryId ? (preferences['subsidiary'][subsidiaryId] || {})[key] : undefined;
      const pAccountLvl = preferences['account'][key];
      const setting = pLocationLvl || pSubsidiaryLvl || pAccountLvl;

      return !setting
        ? res
        : !fields || !Array.isArray(fields)
        ? { ...res, [key]: setting }
        : { ...res, [key]: pick(setting, ...fields) };
    }, {});
  },
);
